import {
  EnvironmentId,
  PullRequestListItem,
  WS_METHODS,
  type ProjectId,
  type PullRequestDiffStat,
  type PullRequestExternalFallback,
  type PullRequestListFailure,
  type PullRequestListFilters,
  type PullRequestListInput,
  type PullRequestListResult,
} from "@afar-research/contracts";
import { matchesPullRequestListItem } from "@afar-research/shared/sourceControl";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { Atom } from "effect/unstable/reactivity";

import { EnvironmentRegistry } from "../connection/registry.ts";
import { GitHubRoutingPermissions } from "../connection/githubRouting.ts";
import { routePullRequestRead } from "./pullRequestRouting.ts";
import {
  createEnvironmentRpcCommand,
  createEnvironmentRpcQueryAtomFamily,
  runInEnvironment,
  runInClientReadinessGeneration,
} from "./runtime.ts";

export interface PullRequestWorkspaceFilters {
  readonly environmentId?: EnvironmentId;
  readonly projectId?: ProjectId;
  readonly state: PullRequestListInput["state"];
  readonly involvement: NonNullable<PullRequestListInput["involvement"]>;
  readonly query?: string;
  readonly filters?: PullRequestListFilters;
}

export interface EnvironmentPullRequestRow extends PullRequestListItem {
  readonly environmentId: EnvironmentId;
  readonly authority: "live" | "retained" | "stale";
  readonly canOpenDetails: boolean;
  readonly additions: number | null;
  readonly deletions: number | null;
}

export interface PullRequestWorkspaceFailure {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId | null;
  readonly projectTitle: string | null;
  readonly failure: PullRequestListFailure;
  readonly externalFallback: PullRequestExternalFallback;
}

export type PullRequestWorkspacePhase =
  | "loading"
  | "ready"
  | "partial-failure"
  | "retained-refresh-failure"
  | "total-failure"
  | "true-empty"
  | "filtered-empty"
  | "unsupported";

export interface PullRequestWorkspaceView {
  readonly phase: PullRequestWorkspacePhase;
  readonly rows: ReadonlyArray<EnvironmentPullRequestRow>;
  readonly failures: ReadonlyArray<PullRequestWorkspaceFailure>;
  readonly statisticsPending: boolean;
}

export interface PullRequestEnvironmentAnswer {
  readonly environmentId: EnvironmentId;
  readonly result: PullRequestListResult;
}

export interface PullRequestEnvironmentFailure {
  readonly environmentId: EnvironmentId;
  readonly message: string;
  readonly stale: boolean;
}

export interface PullRequestWorkspaceSnapshot {
  readonly savedAt: string;
  readonly environmentIds: ReadonlyArray<EnvironmentId>;
  readonly rows: ReadonlyArray<EnvironmentPullRequestRow>;
}

const EnvironmentPullRequestSnapshotRow = Schema.Struct({
  ...PullRequestListItem.fields,
  environmentId: EnvironmentId,
});

const PullRequestWorkspaceSnapshotSchema = Schema.Struct({
  savedAt: Schema.String,
  environmentIds: Schema.Array(EnvironmentId),
  rows: Schema.Array(EnvironmentPullRequestSnapshotRow),
});
const decodePullRequestWorkspaceSnapshot = Schema.decodeUnknownOption(
  PullRequestWorkspaceSnapshotSchema,
);

type SnapshotStorage = Pick<Storage, "getItem" | "setItem">;
const SNAPSHOT_ROW_LIMIT = 100;
const SNAPSHOT_STORAGE_KEY = "afar-research:pull-requests-workspace:v1:last-unsearched";

export function pullRequestEnvironmentSetKey(environmentIds: ReadonlyArray<EnvironmentId>): string {
  return [...environmentIds].sort((left, right) => left.localeCompare(right)).join(",");
}

export function readPullRequestWorkspaceSnapshot(
  storage: SnapshotStorage | undefined,
  environmentIds: ReadonlyArray<EnvironmentId>,
): PullRequestWorkspaceSnapshot | null {
  try {
    const raw = storage?.getItem(SNAPSHOT_STORAGE_KEY);
    if (!raw) return null;
    const decoded = decodePullRequestWorkspaceSnapshot(JSON.parse(raw));
    if (decoded._tag === "None") return null;
    const expectedKey = pullRequestEnvironmentSetKey(environmentIds);
    if (pullRequestEnvironmentSetKey(decoded.value.environmentIds) !== expectedKey) return null;
    return {
      ...decoded.value,
      rows: decoded.value.rows.map((row) => ({
        ...row,
        authority: "stale",
        canOpenDetails: false,
        additions: null,
        deletions: null,
      })),
    };
  } catch {
    return null;
  }
}

export function writePullRequestWorkspaceSnapshot(
  storage: SnapshotStorage | undefined,
  input: {
    readonly environmentIds: ReadonlyArray<EnvironmentId>;
    readonly rows: ReadonlyArray<EnvironmentPullRequestRow>;
    readonly query?: string;
    readonly savedAt: string;
  },
): boolean {
  if (input.query?.trim()) return false;
  try {
    const rows = input.rows
      .slice(0, SNAPSHOT_ROW_LIMIT)
      .map(
        ({
          authority: _authority,
          canOpenDetails: _canOpenDetails,
          additions: _additions,
          deletions: _deletions,
          ...row
        }) => row,
      );
    storage?.setItem(
      SNAPSHOT_STORAGE_KEY,
      JSON.stringify({
        savedAt: input.savedAt,
        environmentIds: input.environmentIds,
        rows,
      }),
    );
    return storage !== undefined;
  } catch {
    return false;
  }
}

export function pullRequestWorkspaceRowKey(
  row: Pick<
    EnvironmentPullRequestRow,
    "environmentId" | "projectId" | "host" | "repository" | "number"
  >,
): string {
  return `${row.environmentId}:${row.projectId}:${row.host}:${row.repository}#${row.number}`;
}

/**
 * What makes a Pull Request itself, following T3Code: the Environment that read it, the host it
 * lives on, its repository and its number. The AFAR Project is deliberately absent — two
 * Projects can be checkouts of one repository, and the same Pull Request read through both is
 * one Pull Request to the reader, not two rows.
 */
export function pullRequestIdentityKey(
  row: Pick<EnvironmentPullRequestRow, "environmentId" | "host" | "repository" | "number">,
): string {
  return `${row.environmentId}:${row.host}:${row.repository}#${row.number}`;
}

/**
 * One row per Pull Request. Where two Projects answered with the same one, the copy that can
 * still be acted on wins: a live read carries permissions and can open a detail, and a retained
 * or stale one cannot, so collapsing to the wrong copy would take those away for no reason.
 */
function collapseToOnePerPullRequest(
  rows: ReadonlyArray<EnvironmentPullRequestRow>,
): ReadonlyArray<EnvironmentPullRequestRow> {
  const byIdentity = new Map<string, EnvironmentPullRequestRow>();
  for (const row of rows) {
    const key = pullRequestIdentityKey(row);
    const held = byIdentity.get(key);
    if (held === undefined) {
      byIdentity.set(key, row);
      continue;
    }
    const heldRank = held.authority === "live" ? 2 : held.authority === "retained" ? 1 : 0;
    const rank = row.authority === "live" ? 2 : row.authority === "retained" ? 1 : 0;
    // A tie keeps the one already held, so a rebuild from the same answers cannot reshuffle
    // which Project a row is attributed to.
    if (rank > heldRank) byIdentity.set(key, row);
  }
  return [...byIdentity.values()];
}

function statisticKey(
  item: Pick<EnvironmentPullRequestRow, "environmentId" | "projectId" | "repository" | "number">,
): string {
  return `${item.environmentId}:${item.projectId}:${item.repository}#${item.number}`;
}

function rowsEqual(left: EnvironmentPullRequestRow, right: EnvironmentPullRequestRow): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function reconcileRows(
  previous: ReadonlyArray<EnvironmentPullRequestRow>,
  next: ReadonlyArray<EnvironmentPullRequestRow>,
): ReadonlyArray<EnvironmentPullRequestRow> {
  const previousByKey = new Map(previous.map((row) => [pullRequestWorkspaceRowKey(row), row]));
  return next.map((row) => {
    const held = previousByKey.get(pullRequestWorkspaceRowKey(row));
    return held !== undefined && rowsEqual(held, row) ? held : row;
  });
}

function matchesWorkspaceFilters(
  row: EnvironmentPullRequestRow,
  input: PullRequestWorkspaceFilters,
): boolean {
  if (input.environmentId !== undefined && row.environmentId !== input.environmentId) return false;
  if (input.projectId !== undefined && row.projectId !== input.projectId) return false;
  return matchesPullRequestListItem(row, input);
}

function hasHostedFilters(input: PullRequestWorkspaceFilters): boolean {
  const qualifiers = input.filters;
  return (
    !!input.query?.trim() ||
    input.involvement !== "all" ||
    qualifiers?.draft !== undefined ||
    qualifiers?.review !== undefined ||
    qualifiers?.checks !== undefined ||
    qualifiers?.author !== undefined ||
    (qualifiers?.labels?.length ?? 0) > 0 ||
    (qualifiers?.excludedLabels?.length ?? 0) > 0
  );
}

function transportFailure(message: string): PullRequestListFailure {
  return {
    reason: "operation-failure",
    action: "retry",
    message,
  };
}

export function derivePullRequestWorkspaceView(input: {
  readonly targetEnvironmentIds: ReadonlyArray<EnvironmentId>;
  readonly answers: ReadonlyArray<PullRequestEnvironmentAnswer>;
  readonly environmentFailures: ReadonlyArray<PullRequestEnvironmentFailure>;
  readonly pending: boolean;
  readonly filters: PullRequestWorkspaceFilters;
  readonly previousRows?: ReadonlyArray<EnvironmentPullRequestRow>;
  readonly snapshot?: PullRequestWorkspaceSnapshot | null;
  readonly statistics?: ReadonlyArray<
    PullRequestDiffStat & { readonly environmentId: EnvironmentId }
  >;
  readonly statisticsPending?: boolean;
}): PullRequestWorkspaceView {
  if (input.targetEnvironmentIds.length === 0) {
    return { phase: "unsupported", rows: [], failures: [], statisticsPending: false };
  }

  const previousRows =
    input.previousRows !== undefined && input.previousRows.length > 0
      ? input.previousRows
      : (input.snapshot?.rows ?? []);
  const previousByProject = new Map<string, EnvironmentPullRequestRow[]>();
  for (const row of previousRows) {
    const key = `${row.environmentId}:${row.projectId}`;
    const held = previousByProject.get(key) ?? [];
    held.push(row);
    previousByProject.set(key, held);
  }

  const failures: PullRequestWorkspaceFailure[] = [];
  const rows: EnvironmentPullRequestRow[] = [];
  let healthyPartitionCount = 0;
  const environmentFailuresById = new Map(
    input.environmentFailures.map((failure) => [failure.environmentId, failure]),
  );
  const answeredEnvironmentIds = new Set<EnvironmentId>();
  for (const answer of input.answers) {
    answeredEnvironmentIds.add(answer.environmentId);
    const environmentFailure = environmentFailuresById.get(answer.environmentId);
    if (environmentFailure !== undefined) {
      for (const partition of answer.result.partitions) {
        if (partition.status !== "ready") continue;
        rows.push(
          ...partition.entries.map((row) => ({
            ...row,
            environmentId: answer.environmentId,
            authority: environmentFailure.stale ? ("stale" as const) : ("retained" as const),
            canOpenDetails: false,
            additions: null,
            deletions: null,
          })),
        );
      }
      continue;
    }
    for (const partition of answer.result.partitions) {
      const projectKey = `${answer.environmentId}:${partition.projectId}`;
      if (partition.status === "unavailable") {
        failures.push({
          environmentId: answer.environmentId,
          projectId: partition.projectId,
          projectTitle: partition.projectTitle,
          failure: partition.failure,
          externalFallback: partition.externalFallback,
        });
        rows.push(
          ...(previousByProject.get(projectKey) ?? []).map((row) => ({
            ...row,
            authority: "retained" as const,
            canOpenDetails: false,
          })),
        );
        continue;
      }
      if (partition.status === "ready") {
        healthyPartitionCount += 1;
        rows.push(
          ...partition.entries.map((row) => ({
            ...row,
            environmentId: answer.environmentId,
            authority: "live" as const,
            canOpenDetails: true,
            additions: null,
            deletions: null,
          })),
        );
      } else if (partition.status === "empty") {
        healthyPartitionCount += 1;
      }
    }
  }

  for (const failure of input.environmentFailures) {
    failures.push({
      environmentId: failure.environmentId,
      projectId: null,
      projectTitle: null,
      failure: transportFailure(failure.message),
      externalFallback: {
        _tag: "Unavailable",
        reason: "repository-url-unavailable",
      },
    });
    if (answeredEnvironmentIds.has(failure.environmentId)) continue;
    for (const row of previousRows) {
      if (row.environmentId !== failure.environmentId) continue;
      rows.push({
        ...row,
        authority: failure.stale ? "stale" : "retained",
        canOpenDetails: false,
        additions: failure.stale ? null : row.additions,
        deletions: failure.stale ? null : row.deletions,
      });
    }
  }

  if (rows.length === 0 && input.answers.length === 0 && input.environmentFailures.length === 0) {
    rows.push(
      ...previousRows.map((row) => ({
        ...row,
        authority: "stale" as const,
        canOpenDetails: false,
        additions: null,
        deletions: null,
      })),
    );
  }

  const stats = new Map(
    (input.statistics ?? []).map((stat) => [statisticKey(stat), stat] as const),
  );
  const withStatistics = rows.map((row) => {
    if (row.authority !== "live") return row;
    const statistic = stats.get(statisticKey(row));
    return statistic === undefined
      ? row
      : { ...row, additions: statistic.additions, deletions: statistic.deletions };
  });
  const filtered = collapseToOnePerPullRequest(
    withStatistics.filter((row) => matchesWorkspaceFilters(row, input.filters)),
  );
  const sorted = filtered.toSorted((left, right) => {
    const recency = right.updatedAt.localeCompare(left.updatedAt);
    return recency === 0
      ? pullRequestWorkspaceRowKey(left).localeCompare(pullRequestWorkspaceRowKey(right))
      : recency;
  });
  const reconciled = reconcileRows(previousRows, sorted);
  const hasFreshRows = reconciled.some((row) => row.authority === "live");
  const hasRetainedRows = reconciled.some((row) => row.authority !== "live");

  let phase: PullRequestWorkspacePhase;
  if (reconciled.length > 0) {
    phase =
      failures.length === 0
        ? "ready"
        : hasFreshRows
          ? "partial-failure"
          : "retained-refresh-failure";
  } else if (input.pending) {
    phase = "loading";
  } else if (failures.length > 0) {
    phase =
      healthyPartitionCount > 0
        ? "partial-failure"
        : failures.every(({ failure }) => failure.reason === "unsupported")
          ? "unsupported"
          : "total-failure";
  } else {
    const unfilteredCount = withStatistics.length;
    // Hosted filtering can remove every row before the client receives the partition.
    phase =
      unfilteredCount > 0 || hasHostedFilters(input.filters) ? "filtered-empty" : "true-empty";
  }
  if (hasRetainedRows && !hasFreshRows && failures.length === 0) {
    phase = "retained-refresh-failure";
  }

  return {
    phase,
    rows: reconciled,
    failures,
    statisticsPending: input.statisticsPending ?? false,
  };
}

/**
 * Runs a pull request read through GitHub sharing when the user has set it up.
 *
 * Falls straight back to the owning Environment when nothing is shared, so a client that has
 * never opened the setting pays neither the permission lookup nor a second code path.
 */
const runRoutedPullRequestRead = <A, E2, R2>(
  environmentId: EnvironmentId,
  effect: Effect.Effect<A, E2, R2>,
) =>
  Effect.gen(function* () {
    const permissionsService = yield* Effect.serviceOption(GitHubRoutingPermissions);
    if (Option.isNone(permissionsService)) {
      return yield* runInEnvironment(environmentId, effect);
    }
    const permissions = yield* permissionsService.value.current;
    if (permissions.length === 0) return yield* runInEnvironment(environmentId, effect);
    const registry = yield* EnvironmentRegistry;
    return yield* routePullRequestRead({
      registry,
      origin: environmentId,
      permissions,
      read: runInClientReadinessGeneration(effect),
    });
  });

export function createPullRequestEnvironmentAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | R, E>,
) {
  return {
    discuss: createEnvironmentRpcCommand(runtime, {
      label: "environment-command:pull-request:discuss",
      tag: WS_METHODS.pullRequestsDiscuss,
    }),
    edit: createEnvironmentRpcCommand(runtime, {
      label: "environment-command:pull-request:edit",
      tag: WS_METHODS.pullRequestsEdit,
      concurrency: {
        mode: "serial",
        key: (target) =>
          [
            target.environmentId,
            target.input.projectId,
            target.input.repository,
            target.input.number,
          ]
            .map(encodeURIComponent)
            .join(":"),
      },
    }),
    list: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:pull-requests:list",
      tag: WS_METHODS.pullRequestsList,
      run: runRoutedPullRequestRead,
      staleTimeMs: 15_000,
      idleTtlMs: 60_000,
    }),
    listStats: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:pull-requests:list-stats",
      tag: WS_METHODS.pullRequestsListStats,
      run: runRoutedPullRequestRead,
      staleTimeMs: 60_000,
      idleTtlMs: 60_000,
    }),
    detail: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:pull-requests:detail",
      tag: WS_METHODS.pullRequestsDetail,
      run: runRoutedPullRequestRead,
      staleTimeMs: 15_000,
      idleTtlMs: 60_000,
    }),
    // Read when a reader opens a row's checks and dropped soon after they close it: nobody
    // scrolling a page of rows should be paying to hold a hundred of these.
    checks: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:pull-requests:checks",
      tag: WS_METHODS.pullRequestsChecks,
      run: runRoutedPullRequestRead,
      staleTimeMs: 15_000,
      idleTtlMs: 30_000,
    }),
    code: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:pull-requests:code",
      tag: WS_METHODS.pullRequestsCode,
      run: runRoutedPullRequestRead,
      staleTimeMs: 60_000,
      idleTtlMs: 0,
    }),
    timeline: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:pull-requests:timeline",
      tag: WS_METHODS.pullRequestsTimeline,
      run: runRoutedPullRequestRead,
      staleTimeMs: 15_000,
      idleTtlMs: 60_000,
    }),
    // Read from the Environment's own projection rather than a host, so it can be asked for
    // often and stays correct the moment a link or unlink lands.
    linkedThreads: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:pull-requests:linked-threads",
      tag: WS_METHODS.pullRequestsLinkedThreads,
      staleTimeMs: 5_000,
      idleTtlMs: 60_000,
    }),
  };
}
